import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useError } from '@/contexts/ErrorContext';

interface QueryErrorStateProps {
  /** The error object from the query */
  error: unknown;
  /** Optional custom message override */
  message?: string;
  /** Optional retry handler */
  onRetry?: () => void;
  /** Optional className for styling */
  className?: string;
}

/**
 * Standardized error display component for query errors.
 * Automatically handles different HTTP status codes and provides user-friendly messages.
 *
 * Usage:
 * ```tsx
 * const { data, error, refetch } = useQuery(...);
 *
 * if (error) {
 *   return <QueryErrorState error={error} onRetry={refetch} />;
 * }
 * ```
 */
export function QueryErrorState({ error, message, onRetry, className }: QueryErrorStateProps) {
  const { t } = useLanguage();
  const { handleApiError } = useError();

  const errorMessage = message ?? handleApiError(error, { context: 'QueryErrorState' }, { showToast: false }).message;

  return (
    <Alert variant="destructive" className={className}>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{t.errors.common.title}</AlertTitle>
      <AlertDescription>
        <div className="space-y-2">
          <p>{errorMessage}</p>
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t.errors.common.retry}
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}
